import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label } from '@1trading/react';
import { Landmark } from 'lucide-react';
import { useId, useState } from 'react';
import { toast } from 'sonner';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { getErrorMessage } from '@/lib/api/error';
import { buildCoinFeeUpdateRequest, type CoinFee, type FeeType, MISSING_FEES_ERROR_CODE, updateCoinFee } from '@/services/api/fees';
import { formatEuros } from '@/utils/format';

// A lone €0 fee trips the upstream falsy-both-fields check — translate
// the raw code into something an operator can act on.
const ZERO_FEE_UNSUPPORTED_MESSAGE = 'The API rejected the update (MISSING_FEES): setting a fee to exactly €0 is currently impossible without a server-side change.';

interface CoinFeeEditDialogProps {
  coinFee: CoinFee;
  feeType: FeeType;
  onClose: () => void;
  /** Refetch fees so the operator can verify the change landed */
  onSubmitted: () => void;
}

// Mounted only while open, so the amount field resets on every open/close.
export const CoinFeeEditDialog = ({ coinFee, feeType, onClose, onSubmitted }: CoinFeeEditDialogProps) => {
  const fieldId = useId();
  const [amount, setAmount] = useState('');

  const coinLabel = coinFee.coin.toUpperCase();
  const currentFee = feeType === 'deposit' ? coinFee.depositFeeEur : coinFee.withdrawalFeeEur;

  const handleUpdate = async () => {
    const trimmed = amount.trim();
    // Update no-ops while the field is empty
    if (trimmed === '') return;

    let failed = false;
    const justification = await confirmAction({
      title: `Confirm Update ${coinLabel} ${feeType} fee`,
      description: `Proceeding with this action will update ${coinLabel} ${feeType} fee to €${trimmed}. Your action here will be logged and will be auditable.`,
      actionLabel: `Update ${coinLabel} ${feeType} fee`,
      onConfirm: async (comment) => {
        try {
          await updateCoinFee(buildCoinFeeUpdateRequest({ coin: coinFee.coin, feeType, amount: trimmed, comment }));
        } catch (error) {
          // Legacy returned 200/null on upstream failure and never
          // checked it — failures looked like successes. Keep the legacy closed-state
          // semantics (both dialogs close either way) BUT surface an error toast.
          failed = true;
          const message = getErrorMessage(error);
          toast.error(message === MISSING_FEES_ERROR_CODE ? ZERO_FEE_UNSUPPORTED_MESSAGE : `Failed to update ${coinLabel} ${feeType} fee: ${message}`);
        }
      },
    });
    if (justification === null) return;

    onClose();
    // The lambda can swallow exceptions into a 200 — refetch so the
    // operator verifies the new value instead of trusting the status code.
    onSubmitted();
    if (!failed) toast.success(`Update submitted for ${coinLabel} ${feeType} fee — verify the refreshed value.`);
  };

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark className="size-5" />
            Editing {feeType.toUpperCase()} fee for {coinLabel}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <p className="text-sm">Current Fee: {formatEuros(currentFee)}</p>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={fieldId}>Enter new fee amount (EUR)</Label>
            <div className="relative">
              <span aria-hidden className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">
                €
              </span>
              <Input className="pl-7" id={fieldId} inputMode="decimal" onChange={(event) => setAmount(event.target.value)} value={amount} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose} type="button" variant="outline">
            Cancel
          </Button>
          <Button onClick={handleUpdate} type="button" variant="primary">
            Update
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
CoinFeeEditDialog.displayName = 'CoinFeeEditDialog';
